import React, { useState } from 'react';
import { 
  Eye, 
  CursorClick, 
  ShoppingCart, 
  CreditCard,
  TrendUp,
  TrendDown,
  Minus,
  ChartLine,
  Funnel,
  Users,
  Clock,
  Globe,
  CheckCircle,
  FileText,
  Lightbulb,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';

// Simple SVG Line Chart component (no external dependencies)
const SimpleLineChart = ({ data, labels, color = '#3B82F6', height = 120 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data, 1);
  const min = 0;
  const range = max - min || 1;
  
  const width = 100;
  const padding = 5;
  const chartWidth = width - padding * 2;
  const chartHeight = height - 20;
  
  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1 || 1)) * chartWidth;
    const y = chartHeight - ((value - min) / range) * (chartHeight - padding);
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none">
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <line
            key={i}
            x1={padding}
            y1={padding + (1 - ratio) * (chartHeight - padding)}
            x2={width - padding}
            y2={padding + (1 - ratio) * (chartHeight - padding)}
            stroke="#E5E7EB"
            strokeWidth="0.5"
          />
        ))}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((value, index) => {
          const x = padding + (index / (data.length - 1 || 1)) * chartWidth;
          const y = chartHeight - ((value - min) / range) * (chartHeight - padding);
          return (
            <circle key={index} cx={x} cy={y} r="2" fill={color} />
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-gray-400 px-1 -mt-1">
        {labels && labels.length > 0 && (
          <>
            <span>{labels[0]?.slice(5)}</span>
            <span>{labels[Math.floor(labels.length / 2)]?.slice(5)}</span>
            <span>{labels[labels.length - 1]?.slice(5)}</span>
          </>
        )}
      </div>
    </div>
  );
};

// KPI Card component
const KPICard = ({ title, value, icon: Icon, color = 'gray', subtitle = null }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    teal: 'bg-[#134E4A]/10 text-[#134E4A]',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    gray: 'bg-gray-50 text-gray-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-[#DCFCE7] text-[#166534]'
  };

  return (
    <div className={`rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={20} />
      </div>
      <p className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-xs opacity-70">{title}</p>
      {subtitle && <p className="text-[10px] opacity-50 mt-1">{subtitle}</p>}
    </div>
  );
};

// Mini KPI for inline stats
const MiniKPI = ({ label, value, color = 'gray' }) => {
  const colorMap = {
    teal: 'text-[#134E4A]',
    green: 'text-[#166534]',
    blue: 'text-blue-600',
    purple: 'text-purple-600',
    amber: 'text-amber-600',
    gray: 'text-gray-600',
    indigo: 'text-indigo-600',
    red: 'text-red-600'
  };
  return (
    <div className="text-center">
      <p className={`text-lg font-bold ${colorMap[color] || colorMap.gray}`}>{value}</p>
      <p className="text-[10px] text-gray-500">{label}</p>
    </div>
  );
};

// Simple data table component
const DataTable = ({ headers, rows, emptyMessage = 'No data' }) => {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-4">{emptyMessage}</p>;
  }
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            {headers.map((h, i) => (
              <th key={i} className="text-left py-2 px-2 text-xs font-medium text-gray-500">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
              {row.map((cell, j) => (
                <td key={j} className="py-2 px-2 text-gray-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Funnel visualization
const FunnelChart = ({ funnel }) => {
  const steps = [
    { key: 'visitors', label: 'Visitors', icon: Eye, color: 'bg-blue-500' },
    { key: 'cta_clicks', label: 'CTA Clicks', icon: CursorClick, color: 'bg-indigo-500' },
    { key: 'checkouts', label: 'Checkout', icon: ShoppingCart, color: 'bg-purple-500' },
    { key: 'purchases', label: 'Purchases', icon: CreditCard, color: 'bg-[#134E4A]' }
  ];

  return (
    <div className="flex items-center justify-between gap-1 sm:gap-2">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const value = funnel[step.key] || 0;
        const prevValue = index > 0 ? funnel[steps[index - 1].key] || 0 : null;
        const conversionRate = prevValue && prevValue > 0 
          ? ((value / prevValue) * 100).toFixed(0) 
          : null;

        return (
          <React.Fragment key={step.key}>
            <div className="flex-1 text-center">
              <div className={`${step.color} text-white rounded-lg p-2 sm:p-3 mb-1`}>
                <Icon size={20} className="mx-auto mb-1" />
                <p className="text-lg sm:text-xl font-bold">{value}</p>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-600 truncate">{step.label}</p>
            </div>
            {index < steps.length - 1 && (
              <div className="flex flex-col items-center px-0.5">
                <span className="text-gray-300 text-lg">→</span>
                {conversionRate && (
                  <span className="text-[9px] text-gray-500">{conversionRate}%</span>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Collapsible Section
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl shadow">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 rounded-t-xl"
      >
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon size={24} />
          {title}
        </h2>
        {isOpen ? <CaretUp size={20} /> : <CaretDown size={20} />}
      </button>
      {isOpen && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
};

// Main Analytics Dashboard Component
export default function AnalyticsDashboard({ analytics, loading }) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500">
        <ChartLine size={32} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">No analytics data available</p>
      </div>
    );
  }

  const { 
    traffic_overview, 
    acquisition, 
    funnel, 
    product_usage, 
    user_table,
    summary, 
    sources, 
    chart, 
    period_days 
  } = analytics;

  // Backward compatibility
  const overview = traffic_overview || {};
  const usage = product_usage || {};
  const acq = acquisition || {};
  const users = user_table || [];

  return (
    <div className="space-y-6">
      {/* Traffic Overview */}
      <CollapsibleSection title="Traffic Overview" icon={Eye} defaultOpen={true}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <KPICard 
            title="Total Page Views" 
            value={overview.total_visitors || summary?.visitors || 0} 
            icon={Eye} 
            color="blue"
          />
          <KPICard 
            title="Unique Visitors" 
            value={overview.unique_visitors || 0} 
            icon={Users} 
            color="indigo"
          />
          <KPICard 
            title="Sessions" 
            value={overview.sessions || 0} 
            icon={Clock} 
            color="purple"
          />
          <KPICard 
            title="Last 24h" 
            value={overview.visitors_24h || 0} 
            icon={TrendUp} 
            color="teal"
          />
          <KPICard 
            title="Last 7 Days" 
            value={overview.visitors_7d || 0} 
            icon={ChartLine} 
            color="blue"
          />
          <KPICard 
            title="Last 30 Days" 
            value={overview.visitors_30d || 0} 
            icon={ChartLine} 
            color="gray"
          />
          <KPICard 
            title="Purchases" 
            value={funnel?.purchases || summary?.purchases || 0} 
            icon={CreditCard} 
            color="teal"
          />
        </div>
        
        {/* Daily Trend Chart */}
        {chart && chart.labels && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Daily Trend (Last {period_days} days)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    Page Views
                  </span>
                </div>
                <SimpleLineChart data={chart.visitors} labels={chart.labels} color="#3B82F6" height={80} />
              </div>
              <div>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#134E4A]"></span>
                    Purchases
                  </span>
                </div>
                <SimpleLineChart data={chart.purchases} labels={chart.labels} color="#134E4A" height={80} />
              </div>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* Acquisition */}
      <CollapsibleSection title="Acquisition" icon={Globe} defaultOpen={true}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Traffic by Source/Medium */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Traffic by Source / Medium</h3>
            {acq.by_source_medium && acq.by_source_medium.length > 0 ? (
              <DataTable
                headers={['Source / Medium', 'Visitors']}
                rows={acq.by_source_medium.map(s => [s.source_medium, s.visitors])}
              />
            ) : (
              <DataTable
                headers={['Source', 'Visitors']}
                rows={sources ? Object.entries(sources).filter(([_, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k, v]) => [k, v]) : []}
                emptyMessage="No traffic source data"
              />
            )}
          </div>
          
          {/* Campaigns */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Campaigns (UTM)</h3>
            <DataTable
              headers={['Campaign', 'Visitors']}
              rows={acq.by_campaign?.map(c => [c.campaign || '(not set)', c.visitors]) || []}
              emptyMessage="No campaign data"
            />
          </div>
          
          {/* Top Referrers */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Top Referrers</h3>
            <DataTable
              headers={['Referrer', 'Visitors']}
              rows={acq.by_referrer?.map(r => [r.referrer, r.visitors]) || []}
              emptyMessage="No referrer data"
            />
          </div>
          
          {/* Landing Pages */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Landing Pages</h3>
            <DataTable
              headers={['Page', 'Visitors']}
              rows={acq.by_landing_page?.map(l => [l.page, l.visitors]) || []}
              emptyMessage="No landing page data"
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Conversion Funnel */}
      <CollapsibleSection title="Conversion Funnel" icon={Funnel} defaultOpen={true}>
        <FunnelChart funnel={funnel || {}} />
        
        {funnel?.rates && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-blue-600">{funnel.rates.visitor_to_cta || funnel.rates.cta_rate || 0}%</p>
              <p className="text-xs text-gray-500">Visitor → CTA</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-purple-600">{funnel.rates.cta_to_checkout || funnel.rates.checkout_rate || 0}%</p>
              <p className="text-xs text-gray-500">CTA → Checkout</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-[#134E4A]">{funnel.rates.checkout_to_purchase || funnel.rates.purchase_rate || 0}%</p>
              <p className="text-xs text-gray-500">Checkout → Purchase</p>
            </div>
            <div className="p-3 bg-[#134E4A]/10 rounded-lg">
              <p className="text-lg font-bold text-[#134E4A]">{funnel.rates.visitor_to_purchase || funnel.rates.overall_rate || 0}%</p>
              <p className="text-xs text-gray-600">Overall Conversion</p>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* Product Usage */}
      <CollapsibleSection title="Product Usage" icon={CheckCircle} defaultOpen={true}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <KPICard 
            title="Total Users" 
            value={usage.total_users || 0} 
            icon={Users} 
            color="blue"
          />
          <KPICard 
            title="Free Users" 
            value={usage.free_users || 0} 
            icon={Users} 
            color="gray"
          />
          <KPICard 
            title="Premium Users" 
            value={usage.premium_users || 0} 
            icon={CreditCard} 
            color="teal"
          />
          <KPICard 
            title="Avg Completion" 
            value={`${usage.avg_completion_pct || 0}%`} 
            icon={CheckCircle} 
            color="purple"
          />
          <KPICard 
            title="Profile Edits" 
            value={usage.profile_edits || 0} 
            icon={FileText} 
            color="amber"
          />
          <KPICard 
            title="Guidance Opens" 
            value={usage.guidance_opens || 0} 
            icon={Lightbulb} 
            color="indigo"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Task Distribution */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Task Completion Distribution</h3>
            <div className="flex items-center justify-around">
              <MiniKPI label="0 Tasks Done" value={usage.users_zero_tasks || 0} color="red" />
              <MiniKPI label="1+ Tasks Done" value={usage.users_with_tasks || 0} color="green" />
            </div>
          </div>
          
          {/* Step Insights */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Step Insights</h3>
            <div className="space-y-2 text-sm">
              {usage.most_completed_step && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Most completed:</span>
                  <span className="font-medium text-[#134E4A] truncate max-w-[60%]" title={usage.most_completed_step.title}>
                    {usage.most_completed_step.title?.substring(0, 25)}... ({usage.most_completed_step.rate?.toFixed(0)}%)
                  </span>
                </div>
              )}
              {usage.least_completed_step && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Least completed:</span>
                  <span className="font-medium text-red-500 truncate max-w-[60%]" title={usage.least_completed_step.title}>
                    {usage.least_completed_step.title?.substring(0, 25)}... ({usage.least_completed_step.rate?.toFixed(0)}%)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* User Table */}
      <CollapsibleSection title="User Table" icon={Users} defaultOpen={false}>
        <div className="text-xs text-gray-500 mb-2">Showing latest 100 users</div>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Email</th>
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Province</th>
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Plan</th>
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Progress</th>
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Created</th>
                <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Source</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? users.map((user, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 px-2 text-gray-700 truncate max-w-[150px]" title={user.email}>
                    {user.email}
                  </td>
                  <td className="py-2 px-2 text-gray-600">{user.province || '-'}</td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${user.plan === 'premium' ? 'bg-[#134E4A]/10 text-[#134E4A]' : 'bg-gray-100 text-gray-600'}`}>
                      {user.plan}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-100 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${user.progress || 0}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{user.progress || 0}%</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-gray-600 text-xs">{user.created_at || '-'}</td>
                  <td className="py-2 px-2 text-gray-600 text-xs">{user.traffic_source || '-'}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-gray-500">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>
    </div>
  );
}
